'use client';

import { Icon } from '@polyseme/design-system';
import { Tooltip } from '@polyseme/design-system';
import { cn } from '@polyseme/utils/cn/cn-utils';
import { motion, easeOut } from 'framer-motion';
import { useTranslations } from 'next-intl';
import Image from 'next/image';
import { useState } from 'react';

import { images } from '~/landing/assets';

import { Bento } from '../components/bento';
import { ProgressStepperDemo } from '../components/progress-stepper-demo';
import { Title } from '../components/title';
import { Typography } from '../components/typography';

type Theme = 'black' | 'blue' | 'green' | 'orange';

const themeOrder: Theme[] = ['black', 'blue', 'orange', 'green'];

const Button = ({
  className,
  theme,
  isSelected,
  ...props
}: React.ComponentProps<'button'> & { theme: Theme; isSelected?: boolean }) => (
  <button
    className={cn(
      'relative grid size-10 place-items-center rounded-full transition-all',
      {
        'bg-black text-white hover:bg-gray-800': theme === 'black',
        'bg-primary text-white hover:bg-primary/80': theme === 'blue',
        'bg-[#006F73] text-white hover:bg-[#005B5B]': theme === 'green',
        'bg-orange-600 text-white hover:bg-orange-500': theme === 'orange',
      },
      isSelected && {
        'ring-2 ring-black ring-offset-2': theme === 'black',
        'ring-2 ring-primary ring-offset-2': theme === 'blue',
        'ring-2 ring-[#006F73] ring-offset-2': theme === 'green',
        'ring-2 ring-orange-600 ring-offset-2': theme === 'orange',
      },
      className
    )}
    {...props}
  />
);

export const BenefitsSection = () => {
  const t = useTranslations('landing.polyseme.benefits');

  const [selectedTheme, setSelectedTheme] = useState<Theme>('blue');

  const handleThemeChange = (newTheme: Theme) => {
    setSelectedTheme(newTheme);
  };

  return (
    <section
      className="mx-auto flex w-full max-w-screen-xl flex-col px-4 py-[60px] lg:px-0"
      id="benefits"
    >
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        transition={{ duration: 0.5, ease: easeOut }}
        viewport={{ once: true, amount: 0.3 }}
        whileInView={{ opacity: 1, y: 0 }}
      >
        <Title
          as="h2"
          className="mb-[60px] text-center md:whitespace-pre-wrap"
          variant="title-2"
        >
          {t('identity.title')}
        </Title>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        transition={{ duration: 0.5, delay: 0.1, ease: easeOut }}
        viewport={{ once: true, amount: 0.3 }}
        whileInView={{ opacity: 1, y: 0 }}
      >
        <Title
          as="h3"
          className="mb-4 whitespace-pre-wrap text-center"
          variant="title-3"
        >
          {t('identity.subtitle')}
        </Title>
        <Typography
          className="whitespace-pre-wrap text-center text-muted-foreground"
          variant="body"
        >
          {t('identity.description')}
        </Typography>
      </motion.div>

      <div className="relative mx-auto mt-10 aspect-video w-full max-w-4xl">
        {themeOrder.map((theme) => (
          <Image
            key={theme}
            fill
            priority
            alt={t('identity.alt', {
              theme: t(`identity.themes.${theme}`),
            })}
            className={cn(
              'rounded-2xl object-contain transition-opacity duration-300',
              theme === selectedTheme ? 'opacity-100' : 'opacity-0'
            )}
            src={images.polyseme.contract[theme]}
          />
        ))}
      </div>

      <motion.div
        className="mx-auto mb-[76px] mt-6 flex flex-wrap gap-4"
        initial={{ opacity: 0, y: 30 }}
        transition={{ duration: 0.5, delay: 0.3, ease: easeOut }}
        viewport={{ once: true, amount: 0.3 }}
        whileInView={{ opacity: 1, y: 0 }}
      >
        {themeOrder.map((theme) => (
          <Button
            key={theme}
            aria-label={t('identity.themes.selectTheme', {
              theme: t(`identity.themes.${theme}`),
            })}
            isSelected={selectedTheme === theme}
            theme={theme}
            onClick={() => handleThemeChange(theme)}
          >
            {selectedTheme === theme && <Icon name="brush" />}
          </Button>
        ))}

        <Tooltip
          content={t('identity.themes.tooltip')}
          position="bottom-center"
          variant="inverted"
        >
          <Button
            aria-label={t('identity.themes.tooltip')}
            className="hidden items-center justify-center bg-gray-100 text-black hover:bg-gray-200 md:inline-flex"
            theme="blue"
          >
            <Icon name="plus" />
          </Button>
        </Tooltip>
      </motion.div>

      <motion.div
        className="mx-auto mt-[76px] max-w-screen-xl"
        initial={{ opacity: 0, y: 30 }}
        transition={{ duration: 0.5, ease: easeOut }}
        viewport={{ once: true, amount: 0.3 }}
        whileInView={{ opacity: 1, y: 0 }}
      >
        <Title
          as="h3"
          className="mb-4 text-center md:whitespace-pre-wrap"
          variant="title-3"
        >
          {t('channels.title')}
        </Title>
        <Typography
          className="mb-10 text-center text-muted-foreground md:whitespace-pre-wrap"
          variant="body"
        >
          {t('channels.description')}
        </Typography>
      </motion.div>
      <motion.div
        className="mx-auto mb-[76px] w-full max-w-[800px]"
        initial={{ opacity: 0, y: 30 }}
        transition={{ duration: 0.5, delay: 0.1, ease: easeOut }}
        viewport={{ once: true, amount: 0.3 }}
        whileInView={{ opacity: 1, y: 0 }}
      >
        <Bento />
      </motion.div>

      <motion.div
        className="mx-auto mt-16 max-w-[800px]"
        initial={{ opacity: 0, y: 30 }}
        transition={{ duration: 0.5, ease: easeOut }}
        viewport={{ once: true, amount: 0.3 }}
        whileInView={{ opacity: 1, y: 0 }}
      >
        <Title
          as="h3"
          className="mb-4 text-center md:whitespace-pre-wrap"
          variant="title-3"
        >
          {t('flux.title')}
        </Title>
        <Typography
          className="mb-10 text-center text-muted-foreground md:whitespace-pre-wrap"
          variant="body"
        >
          {t('flux.description')}
        </Typography>
      </motion.div>

      <ProgressStepperDemo />
    </section>
  );
};
